import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, ViewChild,
} from '@angular/core';
import { NewReleaseFirstSold, FirstSoldEntry } from '../../models/new-release-page.types';

/** One sale on the launch clock: seconds after the sale opened. */
interface TimedSale { rank: number; plate: string; price: number; t: number; }
interface Caption { at: number; ctx: string; body: string; }

/** Hand-written launch stories, by series then plate: the people and readings the data can't
 *  know. {price} and {rank} fill from the sale. These win their moment over the data beats. */
const SERIES_NOTES: Record<string, Record<string, string>> = {
  '76': {
    OO76ARY: 'The first 007 plate goes: <em>OO76 ARY</em>, 007 GARY, for {price}. Six more 007 plates follow inside two minutes.',
    SH76MEE: 'Car vlogger <em>Shmee150</em> (Tim Burton) secures <em>SH76 MEE</em> for {price}, plate {rank} of the 100.',
    KN76HTS: '<em>KN76 HTS</em> reads KNIGHTS: {price}, plate {rank}.',
    OO76OLD: '007 again: <em>OO76 OLD</em>, 007 GOLD, then OO76 UNS and OO76 OLF inside three seconds.',
    AR76NAL: 'Gunners fans are in: <em>AR76 NAL</em>, ARSENAL, for {price}.',
    KR76HNA: '<em>KR76 HNA</em>, KRISHNA, goes for {price}.',
    OO76SPY: '<em>OO76 SPY</em>, 007 SPY, for {price}: the seventh 007 plate of the morning.',
    FR76NDS: '<em>FR76 NDS</em> reads FRIENDS, for {price}.',
  },
};

/** Captions keep at least this much scroll apart, so each stays up long enough to read. */
const CAPTION_GAP_PX = 230;

/** Scroll length per second of launch time: quick through the wait before the first
 *  sale, slow through the first 20 sales (owner: let them land one by one), then the
 *  normal pace for the rest. */
const PX_WAIT = 12;
const PX_EARLY = 70;
const PX_PER_SECOND = 40;
const EARLY_SALES = 20;
const MIN_STAGE_PX = 2400;
const MAX_STAGE_PX = 9000;
/** Rows kept in the stack; older ones have scrolled off the top. */
const STACK_ROWS = 14;

/**
 * "The first 100 plates buyers bought" — the launch-day order book for a
 * series. Pure data: plate + price + an optional "Day N, HH:MM" stamp,
 * chronological (oldest first). No phrase readings, no editorial copy.
 *
 * Data comes from MarketWatch/first-sold-by-series via the page service.
 * `data.rushDuration` is non-null only when the set is a genuine launch
 * rush (>= 100 entries, span < 24h) — see NewReleasePageService; the
 * header drops the "— sold in … from launch" clause when it's null.
 *
 * A genuine rush plays back as you scroll: a launch clock, running counts and
 * the sales stacking up second by second (owner's scrolly_time design,
 * 2026-10-30). Captions are written from the data, so every series gets them, plus
 * hand-written stories in SERIES_NOTES.
 * The full grid stays one tap away, and is all that shows with reduced motion.
 */
@Component({
  selector: 'pla-new-release-first-sold',
  templateUrl: './new-release-first-sold.component.html',
  styleUrls: ['./new-release-first-sold.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewReleaseFirstSoldComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() data!: NewReleaseFirstSold;
  @Input() year: string = '';
  /** Roman numeral for the section eyebrow (page chapter order). */
  @Input() ix: string = '';
  /** Paper / tint band, alternated by the page. */
  @Input() tint: boolean = false;
  /** Readings the page already knows (plate without spaces -> word), for the captions. */
  @Input() terms: Record<string, string> = {};

  @ViewChild('stage') stageRef?: ElementRef<HTMLElement>;

  sales: TimedSale[] = [];
  captions: Caption[] = [];
  /** Launch clock "10:00:00": the hour the sale opened. */
  openClock = '';
  /** Seconds from the sale opening to the last of these sales. */
  total = 0;
  stagePx = MIN_STAGE_PX;

  /** Playback state, driven by scroll. */
  simTime = 0;
  progress = 0;
  soldCount = 0;
  spent = 0;
  caption: Caption | null = null;
  showGrid = false;
  reducedMotion = false;

  /** Scroll pixels -> launch seconds, piecewise: [scroll px at segment start, seconds at start, px per second]. */
  private segments: Array<[number, number, number]> = [];
  /** update() bails out early when nothing moved a whole second, so it can run on every scroll event. */
  private readonly onScroll = () => this.update();

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {
    this.reducedMotion = typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get entries(): FirstSoldEntry[] {
    return this.data?.entries || [];
  }

  get count(): number {
    return this.entries.length;
  }

  /** Fewer than a full 100-sale set — render what exists with a note. */
  get isPartial(): boolean {
    return this.count > 0 && this.count < 100;
  }

  /** The scroll playback runs only for a genuine launch rush with times to the second. */
  get playable(): boolean {
    return !!this.data?.rushDuration && this.sales.length === this.count && !this.reducedMotion;
  }

  /**
   * "The first 100 plates of the 76 series sold in 2 minutes 28 seconds." The duration clause is appended only when
   * the service supplied a rushDuration (full set, genuine launch rush).
   */
  get heading(): string {
    return this.data?.rushDuration
      ? `The first ${this.count} plates of the ${this.year} series sold in ${this.data.rushDuration}.`
      : `The first ${this.count} plates sold in the ${this.year} series.`;
  }

  ngOnChanges(): void {
    this.buildTimeline();
    this.update();
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    });
    this.update();
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  /** Seconds since the sale opened. DVLA opens on the hour, so the clock starts at the
   *  hour of the first sale; times are read straight off the ISO string (wall clock). */
  private buildTimeline(): void {
    const secOf = (iso: string) => {
      const m = /T(\d\d):(\d\d):(\d\d)/.exec(iso || '');
      return m ? +m[1] * 3600 + +m[2] * 60 + +m[3] : NaN;
    };
    const day = (this.entries[0]?.soldDate || '').slice(0, 10);
    const first = secOf(this.entries[0]?.soldDate);
    const open = Math.floor(first / 3600) * 3600;
    const sales = this.entries
      .map((e, i) => ({ rank: i + 1, plate: e.plate, price: e.price || 0, t: secOf(e.soldDate) - open, day: e.soldDate?.slice(0, 10) }))
      .filter((s) => s.day === day && s.t >= 0 && !isNaN(s.t));
    this.sales = sales.map(({ day: _, ...s }) => s);
    this.total = this.sales.length ? this.sales[this.sales.length - 1].t : 0;
    this.openClock = isNaN(open) ? '' : `${String(open / 3600).padStart(2, '0')}:00:00`;
    const t1 = this.sales[0]?.t || 0;
    const tEarly = Math.max(t1, this.sales[Math.min(EARLY_SALES, this.sales.length) - 1]?.t || 0);
    const plan: Array<[number, number]> = [[t1, PX_WAIT], [tEarly - t1, PX_EARLY], [this.total - tEarly, PX_PER_SECOND]];
    let px = 0, sec = 0;
    this.segments = plan.map(([len, rate]) => { const seg: [number, number, number] = [px, sec, rate]; px += len * rate; sec += len; return seg; });
    // Squeeze or stretch evenly to the bounds; the extra viewport height is the sticky hold.
    const fit = Math.max(MIN_STAGE_PX, Math.min(MAX_STAGE_PX, px)) / (px || 1);
    this.segments = this.segments.map(([p, s, r]) => [p * fit, s, r * fit]);
    this.stagePx = px * fit + (typeof window !== 'undefined' ? window.innerHeight : 800);
    this.captions = this.buildCaptions();
    this.caption = this.captions[0] || null;
  }

  /** Commentary for the replay. Hand-written stories for the series win their moment;
   *  the rest is written from the data so every series gets a running story: the wait,
   *  the first plates, plates clearing in the same second, money and plate milestones,
   *  four-figure sales, lulls, runs of the same stem, football plates, the floor price,
   *  halfway, the peak and the last plate. Captions keep CAPTION_GAP_PX of scroll apart. */
  private buildCaptions(): Caption[] {
    const s = this.sales;
    if (!s.length) return [];
    const n = s.length;
    const key = (x: TimedSale) => x.plate.replace(/\s+/g, '');
    const spentBy = (i: number) => s.slice(0, i + 1).reduce((a, x) => a + x.price, 0);
    const name = (x: TimedSale) => {
      const w = this.terms?.[key(x)];
      return `<em>${x.plate}</em>` + (w ? ` (${w.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())})` : '');
    };
    const cands: Array<{ at: number; pri: number; body: string }> = [];
    const add = (at: number, pri: number, body: string) => cands.push({ at, pri, body });

    // Hand-written stories first.
    const notes = SERIES_NOTES[this.year] || {};
    s.forEach((x) => {
      const note = notes[key(x)];
      if (note) add(x.t, 100, note.replace('{price}', this.gbp(x.price)).replace('{rank}', String(x.rank)));
    });

    add(s[0].t, 95, `First plate gone: ${name(s[0])} at ${this.gbp(s[0].price)}.`);
    add(s[n - 1].t, 95, `Plate ${n}: ${name(s[n - 1])}. ${this.gbp(spentBy(n - 1))} spent on the first ${n === 100 ? 'hundred' : n} plates.`);
    const half = Math.floor(n / 2) - 1;
    if (n >= 20) add(s[half].t, 80, `Halfway. <em>${half + 1} plates</em> and ${this.gbp(spentBy(half))} in ${this.duration(s[half].t)}.`);
    const peak = this.busiest(30);
    if (peak.count >= 8) add(peak.end, 80, `The rush peaks: <em>${peak.count} plates</em> clear in thirty seconds.`);
    const top = s.reduce((a, x) => (x.price > a.price ? x : a), s[0]);
    if (top.rank !== 1) add(top.t, 75, `${name(top)} goes for ${this.gbp(top.price)}, the top price of the first ${n}.`);
    if (n >= 10) add(s[4].t, 70, `Five gone in ${this.duration(s[4].t - s[0].t + 1)}: ${s.slice(1, 5).map(name).join(', ')}.`);

    // Several plates clearing in the same second.
    const bySec = new Map<number, TimedSale[]>();
    s.forEach((x) => bySec.set(x.t, [...(bySec.get(x.t) || []), x]));
    const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    [...bySec.values()].filter((g) => g.length >= 3).sort((a, b) => b.length - a.length).slice(0, 2).forEach((g) =>
      add(g[0].t, 60, `${words[g.length] || g.length} plates clear in the same second: ${g.map((x) => `<em>${x.plate}</em>`).join(', ')}.`));

    // Money milestones.
    [10000, 25000, 50000, 100000].forEach((m) => {
      const i = s.findIndex((_, j) => spentBy(j) >= m);
      if (i > 0) add(s[i].t, 55, `<em>${this.gbp(m)}</em> spent, ${i + 1} plates in, ${this.clock(s[i].t)} after the sale opened.`);
    });

    // Plate milestones with the running average.
    [25, 75].filter((m) => m < n).forEach((m) =>
      add(s[m - 1].t, 50, `${m} plates sold, at an average of ${this.gbp(Math.round(spentBy(m - 1) / m))} each.`));

    // Four-figure sales after the first one.
    s.filter((x) => x.price >= 1000).slice(1).forEach((x, i) =>
      add(x.t, 45, `${name(x)} goes for ${this.gbp(x.price)}: four figures, the ${this.ordinal(i + 2)} of the morning.`));

    // The longest lull once buying has started.
    let lull = { gap: 0, at: 0, after: s[0] };
    for (let i = 1; i < n; i++) if (s[i].t - s[i - 1].t > lull.gap) lull = { gap: s[i].t - s[i - 1].t, at: s[i].t, after: s[i - 1] };
    if (lull.gap >= 6) add(lull.at, 40, `A ${lull.gap} second lull after <em>${lull.after.plate}</em>, the longest of the rush. Then the next one goes.`);

    // Runs on the same stem (WR76 HTS, WR76 HTY).
    const stems = new Map<string, TimedSale[]>();
    s.forEach((x) => { const k = key(x).slice(0, 6); stems.set(k, [...(stems.get(k) || []), x]); });
    stems.forEach((g) => {
      if (g.length >= 2) add(g[1].t, 35, `<em>${g[1].plate}</em> follows <em>${g[0].plate}</em> ${this.duration(g[1].t - g[0].t)} later. Someone wanted the set.`);
    });

    // Football plates.
    const fc = s.filter((x) => /(A?FC|UTD)$/.test(key(x)));
    if (fc.length >= 2) add(fc[1].t, 35, `Football plates too: ${fc.map((x) => `<em>${x.plate}</em>`).join(', ')}.`);

    // The floor price.
    const floor = Math.min(...s.map((x) => x.price));
    const atFloor = s.slice(0, half + 1).filter((x) => x.price === floor).length;
    if (n >= 20 && atFloor >= 5) add(s[Math.min(n - 1, 39)].t, 30, `Most buyers aren't chasing rare words: ${atFloor} of the first ${half + 1} plates sold at ${this.gbp(floor)}, the lowest price here.`);

    // The opening wait always shows; then the strongest beats, kept apart so each can be read.
    const picked: Caption[] = [{ at: 0, ctx: this.openClock, body: s[0].t >= 10
      ? `The sale opens. Buyers have their picks lined up. For ${s[0].t} seconds, nothing clears checkout.`
      : 'The sale opens. Buyers have their picks lined up.' }];
    cands.sort((a, b) => b.pri - a.pri || a.at - b.at).forEach((c) => {
      if (picked.every((p) => Math.abs(this.pxAt(p.at) - this.pxAt(c.at)) >= CAPTION_GAP_PX)) picked.push({ at: c.at, ctx: `+${this.clock(c.at)}`, body: c.body });
    });
    return picked.sort((a, b) => a.at - b.at);
  }

  /** Scroll distance into the stage at launch second t (inverse of the pacing in update()). */
  private pxAt(t: number): number {
    const seg = [...this.segments].reverse().find(([, sec]) => t >= sec) || [0, 0, 1];
    return (seg[0] + (t - seg[1]) * seg[2]) / 1.04;
  }

  /** 2 -> "second". */
  private ordinal(v: number): string {
    return ['zeroth', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'][v] || `${v}th`;
  }

  /** Most sales inside any window of `sec` seconds, and the second that window ends. */
  busiest(sec: number): { count: number; end: number } {
    let best = { count: 0, end: 0 };
    for (let i = 0, j = 0; i < this.sales.length; i++) {
      while (this.sales[i].t - this.sales[j].t >= sec) j++;
      if (i - j + 1 > best.count) best = { count: i - j + 1, end: this.sales[i].t };
    }
    return best;
  }

  get topSale(): TimedSale | null {
    return this.sales.reduce<TimedSale | null>((a, x) => (!a || x.price > a.price ? x : a), null);
  }

  get totalSpent(): number {
    return this.sales.reduce((a, x) => a + x.price, 0);
  }

  /** The last few sales, oldest at the top: each new one rises in at the bottom. */
  get stack(): TimedSale[] {
    return this.sales.slice(Math.max(0, this.soldCount - STACK_ROWS), this.soldCount);
  }

  /** "6 May 2026", the launch day. */
  get launchDate(): string {
    const d = Date.parse((this.entries[0]?.soldDate || '').slice(0, 10) + 'T12:00:00Z');
    return isNaN(d) ? '' : new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  }

  /** "10:03:06", when the last of these sales cleared. */
  get lastClock(): string {
    return (this.entries[this.count - 1]?.soldDate || '').slice(11, 19);
  }

  /** 186 -> "three minutes and six seconds". */
  durationWords(t: number): string {
    const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
      'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty'];
    const word = (v: number) => (v < 20 ? ONES[v] : TENS[Math.floor(v / 10)] + (v % 10 ? ' ' + ONES[v % 10] : ''));
    const unit = (v: number, w: string) => `${v < 60 ? word(v) : v} ${w}${v === 1 ? '' : 's'}`;
    const m = Math.floor(t / 60), s = t % 60;
    return m ? (s ? `${unit(m, 'minute')} and ${unit(s, 'second')}` : unit(m, 'minute')) : unit(s, 'second');
  }

  floor(v: number): number { return Math.floor(v); }
  pad(v: number): string { return String(Math.floor(v)).padStart(2, '0'); }

  trackByRank(_: number, s: TimedSale): number { return s.rank; }

  private update(): void {
    const el = this.stageRef?.nativeElement;
    if (!el || !this.playable) return;
    const rect = el.getBoundingClientRect();
    const run = el.offsetHeight - window.innerHeight;
    const scrolled = Math.min(run, Math.max(0, -rect.top));
    const progress = run > 0 ? scrolled / run : 1;
    // Finish just before the stage ends so the last sale always shows.
    const seg = [...this.segments].reverse().find(([p]) => scrolled * 1.04 >= p) || [0, 0, 1];
    const simTime = Math.min(this.total, Math.floor(seg[1] + (scrolled * 1.04 - seg[0]) / seg[2]));
    if (simTime === this.simTime && Math.abs(progress - this.progress) < 0.002) return;
    this.progress = progress;
    this.simTime = simTime;
    let sold = 0, spent = 0;
    for (const s of this.sales) { if (s.t > simTime) break; sold++; spent += s.price; }
    this.soldCount = sold;
    this.spent = spent;
    this.caption = this.captions.filter((c) => c.at <= simTime).pop() || this.captions[0] || null;
    this.zone.run(() => this.cdr.detectChanges());
  }

  toggleGrid(): void {
    this.showGrid = !this.showGrid;
    this.cdr.markForCheck();
  }

  /** 186 -> "3:06". */
  clock(t: number): string {
    return `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;
  }

  /** 186 -> "3 minutes 6 seconds". */
  duration(t: number): string {
    const m = Math.floor(t / 60), s = t % 60;
    const unit = (v: number, w: string) => `${v} ${w}${v === 1 ? '' : 's'}`;
    return m ? (s ? `${unit(m, 'minute')} ${unit(s, 'second')}` : unit(m, 'minute')) : unit(s, 'second');
  }

  round(v: number): number { return Math.round(v); }

  gbp(v: number): string {
    return `£${(v || 0).toLocaleString('en-GB')}`;
  }

  /**
   * "Day 1, 10:14" — day index relative to the first sale's calendar
   * date + the sale's HH:MM. Parsed straight off the ISO string to avoid
   * local-timezone reinterpretation of the recorded wall-clock time.
   */
  dateStamp(entry: FirstSoldEntry): string {
    const iso = entry?.soldDate || '';
    const launch = this.entries[0]?.soldDate || '';
    if (iso.length < 16 || launch.length < 10) return '';
    const time = iso.slice(11, 16); // "HH:MM"
    const d0 = Date.parse(launch.slice(0, 10) + 'T00:00:00Z');
    const dN = Date.parse(iso.slice(0, 10) + 'T00:00:00Z');
    if (isNaN(d0) || isNaN(dN)) return `Day 1, ${time}`;
    const dayNum = Math.round((dN - d0) / 86400000) + 1;
    return `Day ${dayNum}, ${time}`;
  }

  formatPrice(price: number): string {
    return `£${(price || 0).toLocaleString('en-GB')}`;
  }
}
